import { useRequestURL } from '#imports'

import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { useMarketplace } from './useMarketplace'

type MarketDomainTarget = 'main' | 'accounts' | 'payment'

type ValidSubDomain =
  | 'www'
  | 'preprod'
  | `${'accounts' | 'payment'}${'.preprod' | ''}`

function getSubDomain(
  baseTarget: MarketDomainTarget,
  isPreprod: boolean,
): ValidSubDomain {
  if (baseTarget === 'main') {
    return isPreprod ? 'preprod' : 'www'
  }

  return isPreprod ? `${baseTarget}.preprod` : baseTarget
}

/**
 * Based on the current environment, get the base URL of a given target.
 *
 * The targets are defined as follows:
 * - `main` is the main marketplace URL (www.backmarket.TLD or preprod.backmarket.TLD)
 * - `accounts` is the accounts URL (accounts.backmarket.TLD or accounts.preprod.backmarket.TLD)
 * - `payment` is the payment URL (payment.backmarket.TLD or payment.preprod.backmarket.TLD)

 * @example
 * ```ts
 * // current URL: https://www.backmarket.fr/fr-fr
 * const baseUrl = useMarketBaseUrl('payment')
 * // baseUrl > 'https://payment.backmarket.fr'
 * ```
 */
export function useMarketBaseUrl(baseTarget: MarketDomainTarget = 'main') {
  const { protocol, hostname } = useRequestURL()

  const isPreprod =
    hostname.includes('preprod.backmarket.') ||
    // To simplify the local development, local URL will be redirected to preprod
    hostname.includes('local.backmarket.')

  const { market } = useMarketplace()

  const subDomain = getSubDomain(baseTarget, isPreprod)

  const baseUrl = `${protocol}//${subDomain}${market.domain}`
  const baseUrlWithLocale = `${baseUrl}/${toBcp47Locale(market.defaultLocale)}`

  return {
    /**
     * The full URL of the targeted domain. No trailing slash.
     * @example https://www.backmarket.com
     */
    baseUrl,

    /**
     * The full URL of the targeted domain with the locale prefix. No trailing slash.
     * @example https://www.backmarket.com/en-us
     */
    baseUrlWithLocale,
  }
}
